import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";

export type Countdown = {
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
};

function compute(end: Dayjs): Countdown {
  const totalSeconds = Math.max(0, end.diff(dayjs(), "second"));
  return {
    totalSeconds,
    hours: Math.floor(totalSeconds / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

export default function useCountdown(end: Dayjs | undefined): Countdown | null {
  const [value, setValue] = useState<Countdown | null>(() =>
    end ? compute(end) : null,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding end or dropping end?.valueOf() would change when this runs
  useEffect(() => {
    if (!end) {
      setValue(null);
      return undefined;
    }
    setValue(compute(end));
    const id = setInterval(() => setValue(compute(end)), 1000);
    return () => clearInterval(id);
  }, [end?.valueOf()]);

  return value;
}

export function formatCountdown(c: Countdown): string {
  if (c.hours > 0) {
    return `${c.hours}h ${c.minutes}m ${c.seconds}s`;
  }
  if (c.minutes > 0) {
    return `${c.minutes}m ${c.seconds}s`;
  }
  return `${c.seconds}s`;
}

export function formatCountdownMajor(c: Countdown): string {
  if (c.hours > 0) {
    return `${c.hours}h ${c.minutes}m`;
  }
  if (c.minutes > 0) {
    return `${c.minutes}m`;
  }
  return "";
}

export function formatCountdownSeconds(c: Countdown): string {
  return `${c.seconds}s`;
}
